import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ContractTypeResolverService } from '../../services/contract-type-resolver.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { IPartnerContractRole } from 'src/app/domain/registration/interfaces/contract-type.interface';

@Component({
    selector: 'app-contract-role-selection',
    templateUrl: './contract-role-selection.component.html',
    styleUrls: ['./contract-role-selection.component.sass']
})
export class ContractRoleSelectionComponent implements OnInit {
    @Input() partner: any;
    @Input() currentSelection: IPartnerContractRole | null = null;
    @Output() roleSelected = new EventEmitter<IPartnerContractRole>();
    @Output() removeContractClicked = new EventEmitter<IPartnerContractRole>();

    contractRoles: IPartnerContractRole[] = [];
    isLoading = false;

    constructor(
        private contractTypeResolver: ContractTypeResolverService,
        private dialog: MatDialog,
        private toastService: ToastService
    ) { }

    async ngOnInit(): Promise<void> {
        await this.loadContractRoles();
    }

    /**
     * Carrega os papéis de contrato disponíveis para o parceiro
     */
    async loadContractRoles(): Promise<void> {
        console.log('[ContractRoleSelectionComponent] Dados do Parceiro no Input:', this.partner);
        if (!this.partner) {
            console.log('[ContractRoleSelectionComponent] Parceiro nulo ou indefinido, definindo roles como []');
            this.contractRoles = [];
            return;
        }

        this.isLoading = true;
        try {
            this.contractRoles = await this.contractTypeResolver.getPartnerContractRolesWithStatus(this.partner);
            console.log('[ContractRoleSelectionComponent] Roles retornados pelo serviço:', this.contractRoles);
        } catch (error) {
            console.error('Erro ao carregar papéis de contrato:', error);
            this.toastService.showError('Não foi possível carregar os tipos de contrato disponíveis');
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Seleciona um papel de contrato
     * @param role O papel selecionado
     */
    selectContractRole(role: IPartnerContractRole): void {
        // Se já tiver contrato assinado, não permite selecionar
        if (role.hasActiveContract) {
            this.toastService.showWarn(`Este parceiro já possui um contrato de ${role.label} assinado`);
            return;
        }

        // Se tiver contrato em andamento, avisa mas permite continuar
        if (role.contractStatus && role.contractStatus !== 'signed') {
            this.toastService.showInfo(`Existe um contrato de ${role.label} com status: ${this.getStatusLabel(role.contractStatus)}`);
        }

        this.roleSelected.emit(role);
    }

    /**
     * Emite o evento para remover o contrato associado ao papel
     * @param role O papel cujo contrato deve ser removido
     * @param event O evento de clique (para parar a propagação)
     */
    triggerRemoveContract(role: IPartnerContractRole, event: MouseEvent): void {
        event.stopPropagation(); // Impede que o clique selecione o papel também
        this.removeContractClicked.emit(role);
    }

    /**
     * Retorna um label amigável para o status do contrato
     * @param status O status do contrato
     */
    getStatusLabel(status: string): string {
        const statusMap: Record<string, string> = {
            'processing': 'Em Processamento',
            'waiting_for_signatories': 'Aguardando Signatários',
            'waiting_for_signatures': 'Aguardando Assinaturas',
            'link_opened': 'Link Aberto',
            'signed': 'Assinado',
            'archived': 'Arquivado',
            'canceled': 'Cancelado',
            'editing': 'Em Edição',
            'draft': 'Rascunho',
            'expired': 'Expirado',
            'error': 'Erro'
        };

        return statusMap[status] || status;
    }

    /**
     * Retorna a classe CSS para o status do contrato
     * @param role O papel de contrato
     */
    getStatusClass(role: IPartnerContractRole): string {
        if (!role.contractStatus) return '';

        const statusClassMap: Record<string, string> = {
            'processing': 'status-processing',
            'waiting_for_signatories': 'status-waiting-signatories',
            'waiting_for_signatures': 'status-waiting-signatures',
            'link_opened': 'status-viewed',
            'signed': 'status-signed',
            'archived': 'status-archived',
            'canceled': 'status-canceled',
            'editing': 'status-editing',
            'draft': 'status-draft',
            'expired': 'status-expired',
            'error': 'status-error'
        };

        return statusClassMap[role.contractStatus] || 'status-default';
    }
} 